/**
 * FloorPlanStep - Optional step to upload and analyze floor plans
 *
 * This step appears between FloorsStep and SpacesStep in the onboarding wizard.
 * Users can upload floor plan images for each floor to auto-detect spaces.
 * This step is optional - users can skip it entirely.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Upload, Check, SpinnerGap, Warning } from '@phosphor-icons/react';
import { useFloorPlanAnalysis } from '../../../../../hooks/useFloorPlanAnalysis';
import { formatFloorName } from '../../../../../utils/formatters';
import styles from '../../WizardSteps.module.css';
import fpStyles from './FloorPlanStep.module.css';

const FloorPlanStep = ({
  data,
  onChange,
  floorsData,
  projectId,
  savedFloorIds,
  onExtractedSpaces,
  onSkip,
}) => {
  const [selectedFloorIndex, setSelectedFloorIndex] = useState(0);
  const [floorPlanData, setFloorPlanData] = useState(data?.floorPlanData || {});
  const [uploadingFloor, setUploadingFloor] = useState(null);
  const fileInputRef = useRef(null);

  // Get floor list from floorsData
  const floors = floorsData?.floors || [];

  // Get the current floor's DB ID
  const getCurrentFloorDbId = useCallback(
    floorLocalId => {
      return savedFloorIds?.[floorLocalId] || null;
    },
    [savedFloorIds],
  );

  // Current floor
  const currentFloor = floors[selectedFloorIndex];
  const currentFloorDbId = currentFloor ? getCurrentFloorDbId(currentFloor.id) : null;

  // Hook for floor plan analysis
  const {
    isProcessing,
    uploadProgress,
    error,
    analyzeFloorPlan,
    reset: resetAnalysis,
  } = useFloorPlanAnalysis({
    floorId: currentFloorDbId,
    projectId,
  });

  // Handle file selection
  const handleFileSelect = useCallback(
    async (file, floorId) => {
      if (!file || !floorId) return;

      const dbFloorId = getCurrentFloorDbId(floorId);
      if (!dbFloorId) {
        console.error('No database floor ID found for:', floorId);
        return;
      }

      setUploadingFloor(floorId);

      try {
        const result = await analyzeFloorPlan(file, dbFloorId);

        // Store the result
        const updatedData = {
          ...floorPlanData,
          [floorId]: {
            fileName: file.name,
            analysisId: result.analysisId,
            spaces: result.spaces || [],
            comparison: result.comparison,
            status: 'analyzed',
          },
        };

        setFloorPlanData(updatedData);
        onChange({ floorPlanData: updatedData });

        // Pass extracted spaces to parent for SpacesStep prefill
        if (onExtractedSpaces && result.spaces?.length > 0) {
          onExtractedSpaces(floorId, result.spaces);
        }
      } catch (err) {
        // Error handled by hook
        setFloorPlanData(prev => ({
          ...prev,
          [floorId]: {
            ...prev[floorId],
            status: 'error',
            error: err.message,
          },
        }));
      } finally {
        setUploadingFloor(null);
        resetAnalysis();
      }
    },
    [
      analyzeFloorPlan,
      floorPlanData,
      getCurrentFloorDbId,
      onChange,
      onExtractedSpaces,
      resetAnalysis,
    ],
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    e => {
      const file = e.target.files[0];
      if (file && currentFloor) {
        handleFileSelect(file, currentFloor.id);
      }
      e.target.value = ''; // Reset input
    },
    [currentFloor, handleFileSelect],
  );

  // Handle drag and drop
  const handleDrop = useCallback(
    (e, floorId) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file, floorId);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
  }, []);

  // Remove floor plan
  const handleRemoveFloorPlan = useCallback(
    floorId => {
      const updatedData = { ...floorPlanData };
      delete updatedData[floorId];
      setFloorPlanData(updatedData);
      onChange({ floorPlanData: updatedData });
    },
    [floorPlanData, onChange],
  );

  // Get floor plan status
  const getFloorStatus = floorId => {
    const planData = floorPlanData[floorId];
    if (!planData) return 'none';
    return planData.status || 'none';
  };

  // Count analyzed floors
  const analyzedCount = Object.values(floorPlanData).filter(fp => fp.status === 'analyzed').length;

  if (floors.length === 0) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.header}>
          <h1 className={styles.title}>העלאת תוכניות קומה</h1>
          <p className={styles.subtitle}>לא הוגדרו קומות עדיין</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>העלאת תכניות בנייה</h1>
        <p className={styles.subtitle}>
          יש לך תכניות בנייה (אדריכליות / הנדסיות)? העלה אותן ונזהה אוטומטית את החללים בכל קומה.
          <br />
          זה יחסוך לך זמן בהזנת החללים ידנית בשלב הבא.
        </p>
        <div className={fpStyles.optionalBadge}>
          שלב אופציונלי — אפשר להוסיף חללים ידנית בשלב הבא
        </div>
      </div>

      {/* Progress indicator */}
      <div className={fpStyles.progressBar}>
        <div className={fpStyles.progressText}>
          {analyzedCount} מתוך {floors.length} קומות נותחו
        </div>
        <div className={fpStyles.progressTrack}>
          <div
            className={fpStyles.progressFill}
            style={{ width: `${(analyzedCount / floors.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Floor tabs */}
      <div className={fpStyles.floorTabs}>
        {floors.map((floor, index) => {
          const status = getFloorStatus(floor.id);
          return (
            <button
              key={floor.id}
              className={`${fpStyles.floorTab} ${selectedFloorIndex === index ? fpStyles.active : ''} ${status === 'analyzed' ? fpStyles.analyzed : ''}`}
              onClick={() => setSelectedFloorIndex(index)}
            >
              <span className={fpStyles.floorTabName}>
                {floor.name || formatFloorName(floor.floorNum)}
              </span>
              {status === 'analyzed' && (
                <Check size={16} className={fpStyles.checkIcon} weight="bold" />
              )}
            </button>
          );
        })}
      </div>

      {/* Upload area for current floor */}
      {currentFloor && (
        <div className={fpStyles.uploadSection}>
          {uploadingFloor === currentFloor.id || (isProcessing && currentFloorDbId) ? (
            // Uploading/Analyzing state
            <div className={fpStyles.analyzingState}>
              <SpinnerGap size={48} className={fpStyles.spinner} />
              <div className={fpStyles.analyzingText}>
                {uploadProgress < 50 ? 'מעלה קובץ...' : 'מנתח את התוכנית...'}
              </div>
              <div className={fpStyles.progressBarSmall}>
                <div
                  className={fpStyles.progressFillSmall}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : floorPlanData[currentFloor.id]?.status === 'analyzed' ? (
            // Analyzed state
            <div className={fpStyles.analyzedState}>
              <div className={fpStyles.analyzedHeader}>
                <Check size={32} className={fpStyles.successIcon} weight="bold" />
                <div className={fpStyles.analyzedInfo}>
                  <div className={fpStyles.analyzedTitle}>תוכנית נותחה בהצלחה</div>
                  <div className={fpStyles.analyzedSubtitle}>
                    {floorPlanData[currentFloor.id].fileName}
                  </div>
                </div>
              </div>

              {floorPlanData[currentFloor.id].spaces?.length > 0 && (
                <div className={fpStyles.detectedSpaces}>
                  <div className={fpStyles.detectedTitle}>
                    זוהו {floorPlanData[currentFloor.id].spaces.length} חללים:
                  </div>
                  <div className={fpStyles.spacesList}>
                    {floorPlanData[currentFloor.id].spaces.map((space, idx) => (
                      <span key={idx} className={fpStyles.spaceTag}>
                        {space.suggested_name_he || space.suggested_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                className={fpStyles.replaceButton}
                onClick={() => handleRemoveFloorPlan(currentFloor.id)}
              >
                החלף תוכנית
              </button>
            </div>
          ) : floorPlanData[currentFloor.id]?.status === 'error' ? (
            // Error state
            <div className={fpStyles.errorState}>
              <Warning size={48} className={fpStyles.errorIcon} />
              <div className={fpStyles.errorText}>
                {floorPlanData[currentFloor.id].error || 'שגיאה בניתוח התוכנית'}
              </div>
              <button
                className={fpStyles.retryButton}
                onClick={() => handleRemoveFloorPlan(currentFloor.id)}
              >
                נסה שוב
              </button>
            </div>
          ) : (
            // Upload state
            <div
              className={fpStyles.dropZone}
              onDrop={e => handleDrop(e, currentFloor.id)}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={48} className={fpStyles.uploadIcon} />
              <div className={fpStyles.dropText}>גרור תוכנית לכאן או לחץ לבחירה</div>
              <div className={fpStyles.dropHint}>PDF, PNG, JPG עד 50MB</div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Skip button - shown when no floors have been analyzed */}
      {analyzedCount === 0 && (
        <div className={fpStyles.navigationHint}>
          <button type="button" className={fpStyles.skipButton} onClick={onSkip}>
            דלג על שלב זה ←
          </button>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className={fpStyles.errorMessage}>
          <Warning size={18} />
          {error}
        </div>
      )}
    </div>
  );
};

export default FloorPlanStep;
